import React, { useEffect, useRef, useState } from "react";
import * as faceapi from 'face-api.js';
import { AidKitLogo, useInterval } from "./Util";
import { RefreshIcon } from "@heroicons/react/solid";
import { useAPIPost, useAPIUpload } from "./API";
import { useParams } from "react-router";
import { useLocalizedStrings } from "./Localization";

export function WhiteButton(props: {children: React.ReactNode, onClick?: (...args: any) => any}) {
  return <button 
      type="button" 
      onClick={props.onClick || (() => {})} 
      className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" >
     {props.children} 
  </button>
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
function dataURLtoBlob(dataurl: string) {
  var arr = dataurl.split(','), mime = arr[0].match(/:(.*?);/)![1],
      bstr = atob(arr[1]), n = bstr.length, u8arr = new Uint8Array(n);
  while(n--){
      u8arr[n] = bstr.charCodeAt(n);
  }
  return new Blob([u8arr], {type:mime});
}

function getScreenshot(videoEl: HTMLVideoElement): Promise<Blob> {
  const canvas = document.createElement("canvas");
  const settings = ((videoEl.srcObject!) as any).getVideoTracks()[0].getSettings()
  canvas.width = settings.width;
  canvas.height = settings.height;
  canvas.getContext('2d')!.drawImage(videoEl, 0, 0, canvas.width, canvas.height);
  return new Promise((resolve, reject) => {
    canvas.toBlob(blob => {
      resolve(blob!)
    }, "image/jpeg");
  });
}


export default function SelfiePage() {
    const { uid, target } = useParams<{uid: string, target: string}>();
    const upload = useAPIUpload("/upload");
    const saveInfo = useAPIPost("/applicant/" + uid + "/set_info");
    const L = useLocalizedStrings();

    const videoRef = useRef() as React.MutableRefObject<HTMLVideoElement>;
    const [mediaStream, setMediaStream] = useState(null as MediaStream | null);
    const [multipleCameras, setMultipleCameras] = useState(false);
    const [facingMode, setFacingMode] = useState("user" as "user" | "environment");

    const [seenLeft, setSeenLeft] = useState(false);
    const [seenCenter, setSeenCenter] = useState(false);
    const [seenRight, setSeenRight] = useState(false);

    const [modelsLoaded, setModelsLoaded] = useState(false);
    const [leftImage, setLeftImage] = useState(null as Blob | null);
    const [rightImage, setRightImage] = useState(null as Blob | null);
    const [centerImage, setCenterImage] = useState(null as Blob | null);
    const [uploading, setUploading] = useState(false);
    const [uploaded, setUploaded] = useState(false);
    const [failed, setFailed] = useState(false);
    const [cameraBlocked, setCameraBlocked] = useState(false);

    useEffect(() => {
      (async () => {
        await faceapi.loadTinyFaceDetectorModel('/models')
        await faceapi.loadFaceLandmarkTinyModel('/models')
        console.log("Model loaded");
        setModelsLoaded(true);
      })();
    }, []);

    useEffect(() => {
      (async () => {
        if (mediaStream && videoRef.current) {
            console.log("Swapping cameras");
            videoRef.current.pause();
            videoRef.current.srcObject = null;
        }
        const requestedMedia = {
            audio: false,
            video: { facingMode: facingMode },
        };

        let stream;
        try {
          stream = await navigator.mediaDevices.getUserMedia(requestedMedia);
          setMediaStream(stream);
        } catch (e) {
          console.log("No Camera Available 1");
          setCameraBlocked(true);
        }

        let devices;
        try {
          devices = await navigator.mediaDevices.enumerateDevices();
          let videoCount = 0;
          for (const device of devices) {
            if (device.kind === 'videoinput') {
              videoCount++;
            }
          }
          if (videoCount > 1) {
            setMultipleCameras(true);
          }
        } catch (e) {
          console.log("No Camera Available 2");
          setCameraBlocked(true);
        }

        
      })();
    }, [facingMode]);

    useInterval(async () => {
      if(uploaded || !mediaStream || !modelsLoaded) {
        return;
      }
      
      let faces;
      try {
        faces = await faceapi.detectSingleFace(
          videoRef.current, 
          new faceapi.TinyFaceDetectorOptions()).withFaceLandmarks(true)
      } catch (e) {
        console.log("No Camera Available 3");
        setCameraBlocked(true);
      }
      

      if (faces) {
        // The pixel distance between the left ear and tip of the nose
        const left = faces.landmarks.positions[33].x - faces.landmarks.positions[2].x;
        // The pixel distance between the right ear and tip of the nose
        const right = faces.landmarks.positions[14].x - faces.landmarks.positions[33].x;
        const orientation = Math.log(left / right);
        if (orientation > 1.0) {
          setSeenLeft(true);
          if (!leftImage) {
            setLeftImage(await getScreenshot(videoRef.current));
          }
        } else if (orientation < -1.0) {
          setSeenRight(true);
          if (!rightImage) {
            setRightImage(await getScreenshot(videoRef.current));
          }
        } else if (orientation < 0.5 && orientation > -0.5) {
          setSeenCenter(true);
          if (!centerImage) {
            setCenterImage(await getScreenshot(videoRef.current));
          }
        }
        if (/*seenLeft && */ seenCenter /*&& seenRight*/ && !uploaded && !uploading && !failed) {
          setUploading(true);
          try {
            const url = await upload([centerImage]);//, leftImage, rightImage]);
            await saveInfo({
              key: target,
              value: url.urls.join(',')
            });
            setUploaded(true);
            setUploading(false);

            // end the video here
            if (videoRef.current) {
              videoRef.current.pause();
              // mediaStream.getTracks().forEach(function(track) {
              //  track.stop();
              // });
              videoRef.current.srcObject = null;
            }
          } catch (e) {
            console.log("Failed", e)
            setUploaded(false);
            setUploading(false);
            setFailed(true);
          }
        }
      } else if (!seenRight || !seenLeft || !seenCenter) {
        // Maybe clear if we're feeling agressive
      }
      
    }, 1000);
    
  
    if (mediaStream && videoRef.current && !videoRef.current.srcObject) {
      console.log(mediaStream, videoRef.current);
      videoRef.current.srcObject = mediaStream;
    }
  
    function handleCanPlay() {
      console.log("Playing");
      videoRef.current.play();
    }

    function swapCamera() {
      setSeenLeft(false);
      setSeenRight(false);
      setSeenCenter(false);
      setLeftImage(null);
      setCenterImage(null);
      setRightImage(null);
      setUploaded(false);

      if (facingMode === "user") {
        setFacingMode("environment");
      } else {
        setFacingMode("user");
      }
    }
    console.log(modelsLoaded, seenCenter, uploaded, uploading)
  
    return (
      <div className="flex flex-col fixed h-screen w-screen" style={{height: '-webkit-fill-available'}}>
        <div className="flex flex-none">
          <div className="flex-1 text-left">
            <AidKitLogo width={100} height={50} />
          </div>
          <div className="flex-1 text-center self-center">
            { !modelsLoaded ? 
              L.selfie.loading : (
              !seenCenter ?
              L.selfie.look_forward 
              : /*((!seenLeft && !seenRight) ?
                L.selfie.look_to_the_side
                : ((!seenRight || !seenRight) ?
                  L.selfie.look_other_side
                  :*/ (!uploaded ? 
                    (!failed ? L.selfie.submitting : L.selfie.problem_uploading) : L.selfie.done))//))
            }
          </div>
          <div className="flex-1 text-right self-center">
            {multipleCameras &&
              <WhiteButton onClick={swapCamera}>
                <RefreshIcon className="text-gray-800 mr-2" width={30} height={30} />
                {L.selfie.switch_camera}
              </WhiteButton>}
          </div>
        </div>
         {!cameraBlocked && (!uploaded) && (
        <div className="bg-black flex flex-grow" style={{display: uploaded ? 'none' : ''}}>
          <div className="flex-1"></div>
          <video className="flex-none max-h-full max-w-full" 
            style={{objectFit: 'contain', transform: facingMode === 'user' ? 'scaleX(-1)' : ''}} 
            ref={videoRef} onLoadedMetadata={handleCanPlay} onCanPlay={handleCanPlay} autoPlay playsInline muted />
          <div className="flex-1"></div>
        </div>
         )}
        {(seenCenter && uploaded && (
        <div className="flex flex-grow" style={{backgroundColor: "#2B2C60"}}>
          <div className="flex-1"></div>
          <div className="flex-1">
            <h2 className="text-center font-semibold text-white text-4xl"><br /><br /><br /><br />{L.applicant.all_done_you_may_now_close_this_window}</h2>
          </div>
          <div className="flex-1"></div>
        </div>
        ))}
        {(cameraBlocked && (
        <div className="flex flex-grow" style={{backgroundColor: "#2B2C60"}}>
          <div className="flex-1"></div>
          <div className="flex-2">
            <h2 className="text-center font-semibold text-white text-2xl"><br /><br /><br />{L.selfie.camera_disabled}</h2>
          </div>
          <div className="flex-1"></div>
        </div>
        ))}
      </div>
    );
}